/**
 * Klaviyo Tracking Adapter
 *
 * Handles event tracking and user identification for Klaviyo.
 * Uses the existing Klaviyo SDK from lib/klaviyo for consistency.
 * Supports both client-side (browser SDK) and server-side (API) tracking.
 */

'use client';

import type {
  TrackingAdapter,
  TrackingEvent,
  TrackingIdentity,
} from '../types';
import { getKlaviyoMetricName } from '../events';
import {
  klaviyoTrack,
  klaviyoIdentify,
  getAnonymousId,
} from '@/lib/klaviyo/utils';
import { trackAnonymousKlaviyoEvent } from '@/lib/klaviyo/actions';

/**
 * Client-side Klaviyo adapter using existing SDK
 */
export class KlaviyoAdapter implements TrackingAdapter {
  name = 'klaviyo';

  /**
   * Check if Klaviyo SDK is loaded
   * Returns true even if not loaded yet - we'll fallback to server-side
   */
  isAvailable(): boolean {
    // Always return true - we handle client/server fallback in track()
    return true;
  }

  /**
   * Track an event to Klaviyo
   *
   * STRATEGY: Hybrid approach with deduplication
   * - If script is loaded: use client-side only (reliable, immediate)
   * - If script NOT loaded: use server-side backup (ensures event is captured)
   * - This prevents duplicate events for the same user action
   */
  /**
   * Track an event to Klaviyo
   *
   * STRATEGY:
   * - Skip page_view (handled by Klaviyo script as "Active on Site")
   * - All other events: track via client-side (for anonymous + identified users)
   * - If client-side fails: fallback to server-side with anonymous_id
   * - The Klaviyo script automatically handles merge via __kla_id cookie
   */
  async track(event: TrackingEvent): Promise<void> {
    const metricName = getKlaviyoMetricName(event.eventName);

    // Skip if metric name is null (e.g., page_view handled by Klaviyo script)
    if (!metricName) {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          '[Klaviyo] Skipping event (handled by Klaviyo script):',
          event.eventName,
        );
      }
      return;
    }

    const properties = this.formatProperties(event);
    const isScriptLoaded =
      typeof window !== 'undefined' && typeof window.klaviyo !== 'undefined';

    // PRIMARY: Client-side tracking (if script is loaded)
    // Works for both anonymous and identified users
    // The Klaviyo script automatically:
    // - Reads __kla_id cookie for anonymous tracking
    // - Includes email if user was identified via klaviyoIdentify()
    // - Handles the merge automatically
    if (isScriptLoaded) {
      try {
        klaviyoTrack(metricName, properties);

        if (process.env.NODE_ENV === 'development') {
          console.log('[Klaviyo] Client-side event tracked:', metricName);
        }

        // Script handled it successfully - we're done
        return;
      } catch (error) {
        console.warn('[Klaviyo] Client-side tracking failed:', error);
        // Fall through to server-side backup
      }
    }

    // BACKUP: Server-side tracking (only if client-side failed or script not loaded)
    // This should rarely happen in production but provides reliability
    const anonymousId = event.identity?.anonymousId || getAnonymousId();
    if (anonymousId) {
      try {
        await trackAnonymousKlaviyoEvent({
          anonymousId,
          eventName: metricName,
          properties,
        });

        if (process.env.NODE_ENV === 'development') {
          console.log(
            '[Klaviyo] Server-side backup event tracked:',
            metricName,
          );
        }
      } catch (error) {
        console.warn('[Klaviyo] Server-side tracking failed:', error);
      }
    } else {
      console.warn(
        '[Klaviyo] No tracking possible - script not loaded and no anonymous ID',
      );
    }
  }

  /**
   * Identify a user in Klaviyo
   * Client-side SDK automatically handles __kla_id merge
   * No need to manually pass $anonymous
   */
  async identify(identity: TrackingIdentity): Promise<void> {
    if (!identity.email) {
      console.warn('Klaviyo identify requires email');
      return;
    }

    try {
      // Use existing klaviyoIdentify function
      // The Klaviyo SDK automatically reads __kla_id cookie and handles merge
      klaviyoIdentify(identity.email, {
        ...(identity.firstName && { first_name: identity.firstName }),
        ...(identity.lastName && { last_name: identity.lastName }),
        ...(identity.phone && { phone_number: identity.phone }),
        ...identity.properties,
      });
    } catch (error) {
      console.error('Klaviyo identify error:', error);
      throw error;
    }
  }

  /**
   * Format event properties for Klaviyo
   */
  private formatProperties(event: TrackingEvent): Record<string, any> {
    const properties: Record<string, any> = {
      ...event.properties,
    };

    // Add product info if present
    if (event.product) {
      properties.product_id = event.product.productId;
      properties.product_name = event.product.name;
      properties.price = event.product.price;
      properties.currency = event.product.currency;
      properties.category = event.product.category;
      properties.brand = event.product.brand;
      properties.quantity = event.product.quantity;
    }

    // Add order info if present
    if (event.order) {
      properties.order_id = event.order.orderId;
      properties.total = event.order.total;
      properties.subtotal = event.order.subtotal;
      properties.tax = event.order.tax;
      properties.shipping = event.order.shipping;
      properties.discount = event.order.discount;
      properties.currency = event.order.currency;
      properties.coupon = event.order.coupon;

      if (event.order.products) {
        properties.items = event.order.products.map((p) => ({
          product_id: p.productId,
          name: p.name,
          price: p.price,
          quantity: p.quantity,
        }));
      }
    }

    // Add value if present
    if (event.value !== undefined) {
      properties.value = event.value;
    }

    // Add page context
    if (event.url) {
      properties.page_url = event.url;
    }

    if (event.pageTitle) {
      properties.page_title = event.pageTitle;
    }

    return properties;
  }
}

/**
 * Re-export getAnonymousId from Klaviyo SDK for convenience
 */
export { getAnonymousId as getKlaviyoAnonymousId } from '@/lib/klaviyo/utils';
